/* eslint-disable @next/next/no-img-element */
"use client";

import {
  ArrowRight,
  ArrowUpRight,
  Boxes,
  BrainCircuit,
  Check,
  Database,
  Factory,
  FileSearch,
  FileText,
  Headphones,
  History,
  Link2,
  LockKeyhole,
  Network,
  ShieldCheck,
  Sparkles,
  UserCheck,
  Workflow,
} from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  ASSET,
  CLIENT_LOGOS,
  DEMO_URL,
  DOCS_URL,
  LOGIN_URL,
  SIGNUP_URL,
} from "./data";
import { trackLanding } from "./analytics";
import { IndustrySignal, IndustrySignalStyles } from "./IndustrySignal";
import {
  ModelPreview,
  OperatePreview,
  ProductMonitorPreview,
  ProductPreviewStyles,
  SourcesPreview,
} from "./ProductPreview";
import { WayakSignal, WayakSignalStyles } from "./WayakSignal";

const STAGES = [
  {
    key: "connect",
    number: "01",
    label: "Connect",
    title: "Every source becomes working context.",
    body: "Bring warehouses, documents, inboxes, and business apps into one governed operating layer.",
  },
  {
    key: "understand",
    number: "02",
    label: "Understand",
    title: "Agents reason with the full business picture.",
    body: "Link records, rules, history, and evidence so every answer is grounded in how your operation actually works.",
  },
  {
    key: "operate",
    number: "03",
    label: "Operate",
    title: "Turn intelligence into controlled action.",
    body: "Run repeatable playbooks, route exceptions to people, and preserve the reasoning behind every outcome.",
  },
] as const;

type StageIndex = 0 | 1 | 2;

export function Reveal({
  children,
  className = "",
  delay = 0,
  immediate = false,
  style,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  immediate?: boolean;
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }
    if (immediate) {
      const frame = window.requestAnimationFrame(() => setVisible(true));
      return () => window.cancelAnimationFrame(frame);
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [immediate]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(18px)",
        transition: `opacity .64s cubic-bezier(.16,1,.3,1) ${delay}ms, transform .64s cubic-bezier(.16,1,.3,1) ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function WayakMarketingStyles() {
  return (
    <style>{`
      html { scroll-behavior: smooth; }
      .v4-landing {
        --ink: #09090b;
        --paper: #ffffff;
        --warm: #f4f4f5;
        --slate: #09090b;
        --soft: #fafafa;
        --muted: #71717a;
        --line: #e4e4e7;
        --radius-panel: 28px;
        --radius-section: 16px;
        --radius-row: 14px;
        background: var(--paper);
        color: var(--ink);
        font-family: var(--font-sans), Inter, Arial, sans-serif;
        -webkit-font-smoothing: antialiased;
        overflow: clip;
      }
      .v4-landing * { box-sizing: border-box; }
      .v4-landing ::selection { background: #18181b; color: #fff; }
      .v4-landing a, .v4-landing button { -webkit-tap-highlight-color: transparent; }
      .v4-landing a:focus-visible, .v4-landing button:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 4px;
      }
      .v4-shell { width: min(100% - 80px, 1360px); margin: 0 auto; }
      .v4-eyebrow {
        display: flex;
        align-items: center;
        gap: 12px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .18em;
        text-transform: uppercase;
      }
      .v4-eyebrow::before { content: ""; width: 28px; height: 1px; background: currentColor; opacity: .55; }
      .v4-section-title {
        margin: 18px 0 0;
        font-size: clamp(44px, 5vw, 76px);
        line-height: .98;
        letter-spacing: -.045em;
        font-weight: 700;
      }
      .v4-section-intro {
        margin: 24px 0 0;
        max-width: 690px;
        font-size: 18px;
        line-height: 1.6;
        opacity: .68;
      }

      @keyframes v4Pulse { 0%, 100% { opacity: .35; transform: scale(.85); } 50% { opacity: 1; transform: scale(1); } }
      @keyframes v4Flow { 0%,8% { opacity: 0; transform: translateX(-120%); } 12% { opacity: 1; } 58% { opacity: 1; transform: translateX(255%); } 58%,72% { opacity: 1; transform: translateX(255%); } 92% { opacity: 1; transform: translateX(430%); } 100% { opacity: 0; transform: translateX(430%); } }
      @keyframes v4StepSignal { 0%,8%,28%,100% { border-color: rgba(255,255,255,.2); background: transparent; box-shadow: none; transform: scale(1); } 12%,22% { border-color: rgba(255,255,255,.72); background: rgba(255,255,255,.1); box-shadow: 0 0 0 5px rgba(255,255,255,.045); transform: scale(1.04); } }
      @keyframes v4GateSignal { 0%,8%,46%,100% { border-color: rgba(255,255,255,.2); background: transparent; box-shadow: none; transform: scale(1); } 12%,38% { border-color: rgba(255,255,255,.86); background: rgba(255,255,255,.12); box-shadow: 0 0 0 6px rgba(255,255,255,.05); transform: scale(1.04); } }
      @keyframes v4Scene { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
      @keyframes v4Scan { 0% { transform: translateY(-100%); opacity: 0; } 20% { opacity: .35; } 80% { opacity: .35; } 100% { transform: translateY(550%); opacity: 0; } }
      @keyframes v4AmbientBreath { from { opacity: .52; transform: scale(.97); } to { opacity: .86; transform: scale(1.045); } }

      .v4-header {
        position: sticky;
        top: 0;
        z-index: 100;
        height: 68px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 32px;
        background: rgba(255,255,255,.88);
        border-bottom: 1px solid var(--line);
        backdrop-filter: blur(18px) saturate(1.15);
      }
      .v4-header-left, .v4-header-right, .v4-nav { display: flex; align-items: center; }
      .v4-header-left { gap: 42px; }
      .v4-nav { gap: 26px; }
      .v4-nav a, .v4-header-login {
        color: var(--ink);
        text-decoration: none;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: .15em;
        text-transform: uppercase;
        opacity: .66;
        transition: opacity .18s ease;
      }
      .v4-nav a:hover, .v4-header-login:hover { opacity: 1; }
      .v4-header-right { gap: 18px; }
      .v4-header-cta, .v4-button, .v4-button-ghost {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        min-height: 48px;
        padding: 0 22px;
        border: 1px solid currentColor;
        text-decoration: none;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .13em;
        text-transform: uppercase;
        border-radius: 10px;
        transition: transform .2s ease, background .2s ease, color .2s ease, box-shadow .2s ease;
      }
      .v4-header-cta { min-height: 40px; color: #fff; background: var(--ink); border-color: var(--ink); border-radius: 8px; }
      .v4-header-cta:hover, .v4-button:hover { background: #27272a; border-color: #27272a; }
      .v4-header-cta:is(:hover,:focus-visible), .v4-button:is(:hover,:focus-visible), .v4-button-ghost:is(:hover,:focus-visible) { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(9,9,11,.14); }
      .v4-mobile-toggle { display: none; }
      .v4-mobile-nav { display: none; }

      .v4-hero-wrap { padding: 12px; background: var(--paper); }
      .v4-hero {
        position: relative;
        min-height: calc(100vh - 92px);
        border-radius: var(--radius-panel);
        overflow: hidden;
        background:
          radial-gradient(circle at 82% 12%, rgba(255,255,255,.09), transparent 31%),
          #09090b;
        color: #fff;
      }
      .v4-hero::before {
        content: "";
        position: absolute;
        inset: 0;
        background-image: linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px);
        background-size: 48px 48px;
        mask-image: linear-gradient(to bottom, #000, transparent 86%);
      }
      .v4-hero::after { content: ""; position: absolute; z-index: 0; width: min(78vw,980px); aspect-ratio: 1; right: -220px; top: -420px; border-radius: 50%; background: radial-gradient(circle,rgba(255,255,255,.12) 0,rgba(255,255,255,.052) 30%,transparent 68%); pointer-events: none; animation: v4AmbientBreath 16s cubic-bezier(.45,0,.55,1) infinite alternate; will-change: transform,opacity; }
      .v4-hero-mark { z-index: 1; width: 700px; right: -150px; top: -120px; }
      .v4-hero-content { position: relative; z-index: 2; padding: 86px 0 72px; }
      .v4-hero-copy { max-width: 1050px; margin: 0 auto; text-align: center; }
      .v4-hero-copy .v4-eyebrow { justify-content: center; opacity: .65; }
      .v4-hero h1 {
        margin: 28px auto 0;
        max-width: 1040px;
        font-size: clamp(62px, 7.4vw, 108px);
        line-height: .9;
        letter-spacing: -.058em;
        font-weight: 700;
      }
      .v4-hero-sub {
        max-width: 720px;
        margin: 30px auto 0;
        font-size: 19px;
        line-height: 1.6;
        color: rgba(255,255,255,.7);
      }
      .v4-hero-actions { display: flex; justify-content: center; gap: 12px; margin-top: 34px; }
      .v4-button { background: #fff; color: #09090b; border-color: #fff; }
      .v4-button-ghost { color: #fff; border-color: rgba(255,255,255,.28); background: rgba(255,255,255,.035); }
      .v4-button-ghost:hover { background: rgba(255,255,255,.1); }

      .v4-live-canvas {
        position: relative;
        width: min(100%, 1180px);
        margin: 72px auto 0;
        border: 1px solid rgba(255,255,255,.17);
        border-radius: 16px;
        overflow: hidden;
        background: rgba(19,20,22,.92);
        box-shadow: 0 40px 100px rgba(0,0,0,.5);
      }
      .v4-canvas-top {
        height: 52px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 18px;
        border-bottom: 1px solid rgba(255,255,255,.1);
      }
      .v4-canvas-brand, .v4-live-status { display: flex; align-items: center; gap: 10px; }
      .v4-canvas-brand { font-size: 9px; font-weight: 700; letter-spacing: .16em; }
      .v4-live-status { font-size: 9px; letter-spacing: .14em; color: rgba(255,255,255,.58); }
      .v4-live-dot { width: 7px; height: 7px; border-radius: 50%; background: #fff; box-shadow: 0 0 18px rgba(255,255,255,.45); animation: v4Pulse 2.2s ease-in-out infinite; }
      .v4-canvas-body { display: grid; grid-template-columns: .9fr 1.2fr .95fr; min-height: 330px; }
      .v4-canvas-panel { position: relative; padding: 24px; border-right: 1px solid rgba(255,255,255,.09); background: rgba(255,255,255,.015); transition: background .45s ease, opacity .45s ease; }
      .v4-canvas-panel:last-child { border-right: 0; }
      .v4-canvas-panel:not(.is-active) { opacity: .47; }
      .v4-canvas-panel.is-active { background: rgba(255,255,255,.055); opacity: 1; }
      .v4-panel-label { display: flex; justify-content: space-between; font-size: 9px; letter-spacing: .16em; text-transform: uppercase; color: rgba(255,255,255,.45); }
      .v4-panel-label span:last-child { color: rgba(255,255,255,.84); }
      .v4-source-stack { display: grid; gap: 10px; margin-top: 24px; }
      .v4-source {
        display: grid;
        grid-template-columns: 34px 1fr auto;
        align-items: center;
        gap: 11px;
        padding: 12px;
        border: 1px solid rgba(255,255,255,.1);
        background: rgba(0,0,0,.2);
      }
      .v4-source-icon { width: 34px; height: 34px; display: grid; place-items: center; background: rgba(255,255,255,.08); }
      .v4-source strong, .v4-action-list strong { display: block; font-size: 11px; letter-spacing: .02em; }
      .v4-source small, .v4-action-list small { display: block; margin-top: 4px; font-size: 9px; color: rgba(255,255,255,.42); }
      .v4-source-state { font-size: 8px; letter-spacing: .12em; color: rgba(255,255,255,.84); }
      .v4-context-map { position: relative; height: 235px; margin-top: 20px; overflow: hidden; }
      .v4-context-map::after { content: ""; position: absolute; inset: 0; height: 50px; background: linear-gradient(transparent, rgba(255,255,255,.08), transparent); animation: v4Scan 5.2s linear infinite; pointer-events: none; }
      .v4-context-line { position: absolute; height: 1px; transform-origin: left; background: rgba(255,255,255,.22); }
      .v4-context-line.one { width: 42%; left: 29%; top: 43%; transform: rotate(-22deg); }
      .v4-context-line.two { width: 38%; left: 29%; top: 48%; transform: rotate(27deg); }
      .v4-context-line.three { width: 34%; left: 38%; top: 70%; transform: rotate(-13deg); }
      .v4-node { position: absolute; min-width: 98px; padding: 12px; border: 1px solid rgba(255,255,255,.14); background: #17191c; }
      .v4-node strong { font-size: 10px; }
      .v4-node small { display: block; margin-top: 4px; font-size: 8px; color: rgba(255,255,255,.42); }
      .v4-node.main { left: 30%; top: 36%; border-color: rgba(255,255,255,.55); box-shadow: 0 0 30px rgba(255,255,255,.07); }
      .v4-node.a { left: 2%; top: 5%; }
      .v4-node.b { right: 1%; top: 8%; }
      .v4-node.c { left: 7%; bottom: 4%; }
      .v4-node.d { right: 3%; bottom: 4%; }
      .v4-action-list { display: grid; gap: 0; margin-top: 20px; }
      .v4-action-row { position: relative; display: grid; grid-template-columns: 26px 1fr; gap: 10px; padding: 11px 0; border-bottom: 1px solid rgba(255,255,255,.08); }
      .v4-action-mark { width: 22px; height: 22px; display: grid; place-items: center; border: 1px solid rgba(255,255,255,.2); font-size: 9px; }
      .v4-action-row.done .v4-action-mark { background: #fff; border-color: #fff; color: var(--ink); }
      .v4-approval { margin-top: 14px; padding: 12px; border: 1px solid rgba(255,255,255,.3); background: rgba(255,255,255,.05); }
      .v4-approval small { display: block; font-size: 8px; letter-spacing: .14em; color: rgba(255,255,255,.82); }
      .v4-approval strong { display: block; margin-top: 6px; font-size: 11px; }
      .v4-canvas-controls { display: grid; grid-template-columns: repeat(3, 1fr); border-top: 1px solid rgba(255,255,255,.1); }
      .v4-stage-button {
        position: relative;
        min-height: 72px;
        display: grid;
        grid-template-columns: 30px 1fr;
        align-items: center;
        gap: 8px;
        padding: 0 18px;
        border: 0;
        border-right: 1px solid rgba(255,255,255,.1);
        background: transparent;
        color: rgba(255,255,255,.5);
        text-align: left;
        cursor: pointer;
        transition: color .25s ease, background .25s ease;
      }
      .v4-stage-button:last-child { border-right: 0; }
      .v4-stage-button.is-active { color: #fff; background: rgba(255,255,255,.05); }
      .v4-stage-button span { font-size: 9px; letter-spacing: .12em; }
      .v4-stage-button strong { font-size: 11px; letter-spacing: .12em; text-transform: uppercase; }
      .v4-stage-progress { position: absolute; left: 0; right: 0; bottom: 0; height: 2px; background: rgba(255,255,255,.08); }
      .v4-stage-progress::after { content: ""; display: block; width: var(--progress); height: 100%; background: #fff; transition: width .5s cubic-bezier(.2,0,0,1); }

      .v4-network { padding: 38px 0; border-bottom: 1px solid var(--line); }
      .v4-network-row { display: flex; align-items: center; gap: 36px; }
      .v4-network-label { flex: 0 0 190px; font-size: 10px; line-height: 1.5; letter-spacing: .15em; text-transform: uppercase; opacity: .45; }
      .v4-network-logos { flex: 1; display: flex; align-items: center; justify-content: space-between; gap: 28px; }
      .v4-network-logos img { display: block; max-width: 112px; max-height: 32px; width: auto; filter: grayscale(1); mix-blend-mode: multiply; opacity: .55; }

      .v4-manifesto { position: relative; padding: 150px 0; overflow: hidden; border-bottom: 1px solid var(--line); background: var(--soft); color: var(--ink); }
      .v4-manifesto::after { content: "WAYAK"; position: absolute; right: -40px; bottom: -54px; font-size: 240px; font-weight: 700; letter-spacing: -.08em; color: rgba(9,9,11,.035); }
      .v4-manifesto-copy { max-width: 1120px; }
      .v4-manifesto blockquote { margin: 32px 0 0; font-size: clamp(48px, 6vw, 86px); line-height: 1.02; letter-spacing: -.045em; font-weight: 500; }
      .v4-manifesto em { font-family: inherit; font-style: normal; font-weight: 300; color: #52525b; }

      .v4-platform { padding: 136px 0 150px; background: var(--warm); }
      .v4-platform-grid { display: grid; grid-template-columns: .78fr 1.22fr; gap: 70px; align-items: start; margin-top: 72px; }
      .v4-story-tabs { position: sticky; top: 106px; display: grid; border-top: 1px solid rgba(10,10,10,.18); }
      .v4-story-tab {
        display: grid;
        grid-template-columns: 44px 1fr;
        gap: 14px;
        padding: 24px 4px;
        border: 0;
        border-bottom: 1px solid rgba(10,10,10,.18);
        background: transparent;
        color: rgba(10,10,10,.43);
        text-align: left;
        cursor: pointer;
        transition: color .25s ease, padding-left .25s ease;
      }
      .v4-story-tab.is-active { color: #000; padding-left: 14px; }
      .v4-story-tab span { font-size: 10px; letter-spacing: .12em; }
      .v4-story-tab strong { display: block; font-size: 14px; letter-spacing: .12em; text-transform: uppercase; }
      .v4-story-tab small { display: block; margin-top: 8px; max-width: 330px; font-size: 13px; line-height: 1.5; }
      .v4-story-stage { min-height: 570px; padding: 24px; border: 1px solid var(--line); border-radius: var(--radius-panel); background: #fff; box-shadow: 0 24px 80px rgba(9,9,11,.08); }
      .v4-story-stage-top { display: flex; align-items: center; justify-content: space-between; padding-bottom: 18px; border-bottom: 1px solid rgba(10,10,10,.12); font-size: 9px; letter-spacing: .15em; text-transform: uppercase; }
      .v4-scene { min-height: 470px; padding: 38px 20px 12px; animation: v4Scene .46s cubic-bezier(.16,1,.3,1) both; }
      .v4-scene h3 { margin: 0; font-size: 38px; line-height: 1.05; letter-spacing: -.035em; }
      .v4-scene > p { max-width: 540px; margin: 14px 0 30px; font-size: 15px; line-height: 1.55; opacity: .58; }
      .v4-connect-scene { display: grid; grid-template-columns: 1fr 80px 1fr; gap: 16px; align-items: center; margin-top: 34px; }
      .v4-system-stack { display: grid; gap: 10px; }
      .v4-system-card { display: flex; align-items: center; gap: 12px; padding: 15px; border: 1px solid rgba(10,10,10,.13); background: #fff; }
      .v4-system-card strong { font-size: 12px; }
      .v4-system-card small { display: block; margin-top: 3px; font-size: 10px; opacity: .45; }
      .v4-join { position: relative; height: 1px; background: #000; }
      .v4-join::after { content: ""; position: absolute; right: -1px; top: -4px; width: 8px; height: 8px; border-top: 1px solid #000; border-right: 1px solid #000; transform: rotate(45deg); }
      .v4-context-record { padding: 28px; background: var(--ink); color: #fff; box-shadow: 12px 12px 0 rgba(9,9,11,.08); }
      .v4-context-record small { font-size: 9px; letter-spacing: .14em; color: rgba(255,255,255,.78); }
      .v4-context-record strong { display: block; margin-top: 12px; font-size: 22px; }
      .v4-record-tags { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 22px; }
      .v4-record-tags span { padding: 7px 9px; border: 1px solid rgba(255,255,255,.2); font-size: 9px; color: rgba(255,255,255,.66); }
      .v4-graph-scene { position: relative; height: 280px; margin-top: 34px; border: 1px solid rgba(10,10,10,.12); background-image: radial-gradient(rgba(10,10,10,.16) .7px, transparent .7px); background-size: 18px 18px; overflow: hidden; }
      .v4-graph-node { position: absolute; min-width: 120px; padding: 14px; border: 1px solid rgba(10,10,10,.16); background: #fff; box-shadow: 0 10px 30px rgba(10,10,10,.08); }
      .v4-graph-node strong { display: block; font-size: 12px; }
      .v4-graph-node small { display: block; margin-top: 4px; font-size: 9px; opacity: .44; }
      .v4-graph-node.center { left: 42%; top: 38%; background: var(--ink); color: #fff; border-color: var(--ink); }
      .v4-graph-node.one { left: 6%; top: 10%; }
      .v4-graph-node.two { right: 7%; top: 8%; }
      .v4-graph-node.three { left: 13%; bottom: 10%; }
      .v4-graph-node.four { right: 10%; bottom: 12%; }
      .v4-graph-path { position: absolute; height: 1px; background: rgba(10,10,10,.35); transform-origin: left; }
      .v4-graph-path.p1 { width: 38%; left: 17%; top: 29%; transform: rotate(18deg); }
      .v4-graph-path.p2 { width: 31%; left: 52%; top: 43%; transform: rotate(-25deg); }
      .v4-graph-path.p3 { width: 32%; left: 21%; top: 69%; transform: rotate(-18deg); }
      .v4-graph-path.p4 { width: 28%; left: 54%; top: 59%; transform: rotate(22deg); }
      .v4-playbook-scene { display: grid; grid-template-columns: 1fr .75fr; gap: 18px; margin-top: 30px; }
      .v4-playbook-list { border: 1px solid rgba(10,10,10,.13); background: #fff; }
      .v4-playbook-row { display: grid; grid-template-columns: 32px 1fr auto; gap: 10px; align-items: center; padding: 15px; border-bottom: 1px solid rgba(10,10,10,.1); }
      .v4-playbook-row:last-child { border-bottom: 0; }
      .v4-playbook-check { width: 24px; height: 24px; display: grid; place-items: center; border: 1px solid rgba(10,10,10,.2); }
      .v4-playbook-row.done .v4-playbook-check { background: #000; color: #fff; }
      .v4-playbook-row strong { font-size: 11px; }
      .v4-playbook-row small { display: block; margin-top: 3px; font-size: 9px; opacity: .45; }
      .v4-playbook-row > span:last-child { font-size: 8px; letter-spacing: .1em; opacity: .48; }
      .v4-human-gate { display: flex; flex-direction: column; justify-content: space-between; padding: 20px; background: var(--ink); color: #fff; }
      .v4-human-gate small { font-size: 9px; letter-spacing: .14em; color: rgba(255,255,255,.78); }
      .v4-human-gate strong { display: block; margin-top: 12px; font-size: 23px; line-height: 1.12; }
      .v4-human-gate p { margin: 14px 0 0; font-size: 11px; line-height: 1.5; color: rgba(255,255,255,.52); }
      .v4-approve-chip { display: flex; align-items: center; gap: 8px; margin-top: 26px; padding: 11px; background: #fff; color: var(--ink); font-size: 9px; font-weight: 700; letter-spacing: .11em; text-transform: uppercase; }

      .v4-workflow { position: relative; padding: 142px 0; overflow: hidden; background: var(--ink); color: #fff; }
      .v4-workflow > .v4-shell { position: relative; z-index: 2; }
      .v4-workflow .v4-section-intro { color: rgba(255,255,255,.66); }
      .v4-flow { position: relative; display: grid; grid-template-columns: repeat(5, 1fr); margin-top: 76px; border-top: 1px solid rgba(255,255,255,.16); border-bottom: 1px solid rgba(255,255,255,.16); }
      .v4-flow::before { content: ""; position: absolute; left: 0; top: -1px; width: 24%; height: 1px; overflow: hidden; background: linear-gradient(90deg, transparent, rgba(255,255,255,.95), transparent); }
      .v4-flow.is-active::before { animation: v4Flow 8.8s cubic-bezier(.45,0,.2,1) infinite; }
      .v4-flow-step { min-height: 270px; padding: 24px; border-right: 1px solid rgba(255,255,255,.14); }
      .v4-flow-step:last-child { border-right: 0; }
      .v4-flow-step > span { font-size: 10px; letter-spacing: .13em; color: rgba(255,255,255,.82); }
      .v4-flow-icon { width: 42px; height: 42px; display: grid; place-items: center; margin-top: 34px; border: 1px solid rgba(255,255,255,.2); }
      .v4-flow.is-active .v4-flow-step:nth-child(1) .v4-flow-icon { animation: v4StepSignal 8.8s cubic-bezier(.16,1,.3,1) .2s infinite; }
      .v4-flow.is-active .v4-flow-step:nth-child(2) .v4-flow-icon { animation: v4StepSignal 8.8s cubic-bezier(.16,1,.3,1) 1.5s infinite; }
      .v4-flow.is-active .v4-flow-step:nth-child(3) .v4-flow-icon { animation: v4StepSignal 8.8s cubic-bezier(.16,1,.3,1) 2.8s infinite; }
      .v4-flow.is-active .v4-flow-step:nth-child(4) .v4-flow-icon { animation: v4GateSignal 8.8s cubic-bezier(.16,1,.3,1) 4.1s infinite; }
      .v4-flow.is-active .v4-flow-step:nth-child(5) .v4-flow-icon { animation: v4StepSignal 8.8s cubic-bezier(.16,1,.3,1) 6.3s infinite; }
      .v4-flow-step h3 { margin: 22px 0 0; font-size: 14px; letter-spacing: .12em; text-transform: uppercase; }
      .v4-flow-step p { margin: 11px 0 0; font-size: 13px; line-height: 1.55; color: rgba(255,255,255,.5); }
      .v4-flow-note { margin-top: 28px; text-align: right; font-family: inherit; font-size: 18px; font-style: normal; font-weight: 300; color: rgba(255,255,255,.55); }

      .v4-use-cases { padding: 142px 0 156px; background: var(--paper); }
      .v4-case-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-top: 72px; }
      .v4-case {
        position: relative;
        min-height: 520px;
        display: flex;
        flex-direction: column;
        padding: 30px;
        overflow: hidden;
        border: 1px solid var(--line);
        border-radius: var(--radius-section);
        background: #fff;
        color: var(--ink);
        text-decoration: none;
        transition: transform .34s cubic-bezier(.16,1,.3,1), border-color .26s ease, background .26s ease, box-shadow .34s ease;
      }
      .v4-case:is(:hover,:focus-visible) { transform: translateY(-3px); border-color: rgba(9,9,11,.3); background: var(--soft); box-shadow: 0 18px 44px rgba(9,9,11,.07); }
      .v4-case.dark, .v4-case.warm, .v4-case.light { background: #fff; color: var(--ink); }
      .v4-case-index { display: flex; align-items: center; justify-content: space-between; font-size: 10px; letter-spacing: .14em; text-transform: uppercase; opacity: .54; }
      .v4-case .iv-scene { margin: 32px 0; }
      .v4-case h3 { margin: 0; font-size: 30px; line-height: 1.05; letter-spacing: -.03em; }
      .v4-case > p { margin: 16px 0 0; font-size: 14px; line-height: 1.55; opacity: .62; }
      .v4-case-meta { display: flex; flex-wrap: wrap; gap: 6px; margin-top: auto; padding-top: 28px; }
      .v4-case-meta span { padding: 7px 9px; border: 1px solid currentColor; font-size: 8px; letter-spacing: .1em; text-transform: uppercase; opacity: .54; }

      .v4-governance { padding: 140px 0; background: var(--soft); }
      .v4-governance-grid { display: grid; grid-template-columns: .85fr 1.15fr; gap: 80px; align-items: start; }
      .v4-control-list { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .v4-control { min-height: 220px; padding: 26px; border: 1px solid var(--line); border-radius: var(--radius-section); background: #fff; }
      .v4-control-icon { width: 38px; height: 38px; display: grid; place-items: center; border-radius: 10px; background: var(--ink); color: #fff; }
      .v4-control h3 { margin: 28px 0 0; font-size: 15px; letter-spacing: .1em; text-transform: uppercase; }
      .v4-control p { margin: 12px 0 0; font-size: 13px; line-height: 1.55; opacity: .58; }

      .v4-delivery { padding: 142px 0; background: var(--paper); }
      .v4-delivery-head { display: flex; align-items: end; justify-content: space-between; gap: 40px; }
      .v4-delivery-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 72px; }
      .v4-delivery-step { min-height: 260px; padding: 28px; border: 1px solid var(--line); border-radius: var(--radius-section); background: var(--soft); }
      .v4-delivery-step:last-child { border-right: 1px solid var(--line); }
      .v4-delivery-step span { font-family: inherit; font-size: 40px; font-style: normal; font-weight: 300; font-variant-numeric: tabular-nums; }
      .v4-delivery-step h3 { margin: 42px 0 0; font-size: 15px; letter-spacing: .12em; text-transform: uppercase; }
      .v4-delivery-step p { margin: 12px 0 0; font-size: 14px; line-height: 1.55; opacity: .58; }
      .v4-inline-link { display: inline-flex; align-items: center; gap: 9px; color: inherit; font-size: 11px; font-weight: 700; letter-spacing: .13em; text-transform: uppercase; text-decoration: none; }
      .v4-inline-link:hover svg { transform: translate(3px,-3px); }
      .v4-inline-link svg { transition: transform .2s ease; }

      .v4-closing { padding: 16px 16px 0; background: var(--paper); }
      .v4-closing-panel { position: relative; min-height: 600px; display: grid; place-items: center; padding: 90px 40px; overflow: hidden; border-radius: var(--radius-panel) var(--radius-panel) 0 0; background: var(--ink); color: #fff; text-align: center; }
      .v4-closing-panel::before { content: ""; position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,.12) .7px, transparent .7px); background-size: 20px 20px; mask-image: radial-gradient(circle at center, #000, transparent 70%); }
      .v4-closing-content { position: relative; z-index: 2; max-width: 950px; }
      .v4-closing h2 { margin: 0; font-size: clamp(56px, 7vw, 102px); line-height: .92; letter-spacing: -.055em; }
      .v4-closing p { max-width: 590px; margin: 28px auto 0; font-size: 18px; line-height: 1.55; color: rgba(255,255,255,.62); }
      .v4-closing-actions { display: flex; justify-content: center; gap: 12px; margin-top: 34px; }

      .v4-footer { margin: 0 16px 16px; padding: 72px 0 30px; border-radius: 0 0 var(--radius-panel) var(--radius-panel); background: var(--ink); color: #fff; }
      .v4-footer-grid { display: grid; grid-template-columns: 1.4fr repeat(3, .6fr); gap: 60px; padding-bottom: 70px; }
      .v4-footer-brand p { max-width: 350px; margin: 22px 0 0; font-size: 14px; line-height: 1.6; color: rgba(255,255,255,.48); }
      .v4-footer h3 { margin: 0 0 18px; font-size: 9px; letter-spacing: .15em; text-transform: uppercase; color: rgba(255,255,255,.42); }
      .v4-footer-links { display: grid; gap: 11px; }
      .v4-footer-links a { color: rgba(255,255,255,.72); text-decoration: none; font-size: 13px; }
      .v4-footer-links a:hover { color: #fff; }
      .v4-footer-bottom { display: flex; align-items: center; justify-content: space-between; padding-top: 24px; border-top: 1px solid rgba(255,255,255,.12); font-size: 10px; letter-spacing: .1em; color: rgba(255,255,255,.4); }
      .v4-footer-bottom a { color: inherit; text-decoration: none; }

      @media (max-width: 1080px) {
        .v4-nav { gap: 16px; }
        .v4-hero h1 { font-size: clamp(58px, 8.5vw, 86px); }
        .v4-canvas-body { grid-template-columns: .9fr 1.1fr; }
        .v4-canvas-panel:last-child { grid-column: 1 / -1; border-top: 1px solid rgba(255,255,255,.09); }
        .v4-platform-grid, .v4-governance-grid { grid-template-columns: 1fr; }
        .v4-story-tabs { position: static; grid-template-columns: repeat(3, 1fr); }
        .v4-story-tab { grid-template-columns: 28px 1fr; }
        .v4-story-tab small { display: none; }
        .v4-case-grid { grid-template-columns: 1fr; }
        .v4-case { min-height: 440px; }
        .v4-case .iv-scene { max-width: 540px; }
        .v4-flow { grid-template-columns: repeat(3, 1fr); }
        .v4-flow-step:nth-child(3) { border-right: 0; }
        .v4-flow-step:nth-child(n+4) { border-top: 1px solid rgba(255,255,255,.14); }
        .v4-footer-grid { grid-template-columns: 1.3fr 1fr 1fr; }
        .v4-footer-brand { grid-column: 1 / -1; }
      }

      @media (max-width: 820px) {
        .v4-shell { width: min(100% - 40px, 1360px); }
        .v4-header { height: 62px; padding: 0 18px; }
        .v4-nav, .v4-header-login, .v4-header-cta { display: none; }
        .v4-mobile-toggle { display: flex; width: 38px; height: 38px; align-items: center; justify-content: center; border: 1px solid rgba(10,10,10,.16); background: transparent; color: #000; }
        .v4-mobile-nav.is-open { position: fixed; display: grid; left: 0; right: 0; top: 62px; z-index: 99; padding: 20px; border-bottom: 1px solid var(--line); background: rgba(255,255,255,.98); box-shadow: 0 18px 40px rgba(0,0,0,.08); }
        .v4-mobile-nav a { display: flex; align-items: center; justify-content: space-between; padding: 15px 0; border-bottom: 1px solid var(--line); color: #000; text-decoration: none; font-size: 11px; font-weight: 700; letter-spacing: .13em; text-transform: uppercase; }
        .v4-mobile-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding-top: 18px; }
        .v4-mobile-actions a { justify-content: center; border: 1px solid #000; padding: 13px 8px; }
        .v4-mobile-actions a:last-child { background: #000; color: #fff; }
        .v4-hero-wrap { padding: 8px; }
        .v4-hero { min-height: auto; border-radius: 15px; }
        .v4-hero-content { padding: 70px 0 34px; }
        .v4-hero h1 { font-size: clamp(46px, 13vw, 72px); line-height: .94; }
        .v4-hero-sub { font-size: 16px; }
        .v4-hero-actions, .v4-closing-actions { flex-direction: column; }
        .v4-hero-actions a, .v4-closing-actions a { width: 100%; }
        .v4-live-canvas { margin-top: 54px; }
        .v4-canvas-top { padding: 0 12px; }
        .v4-live-status { display: none; }
        .v4-canvas-body { grid-template-columns: 1fr; min-height: 300px; }
        .v4-canvas-panel { display: none; min-height: 300px; border-right: 0; }
        .v4-canvas-panel.is-active { display: block; }
        .v4-canvas-controls { grid-template-columns: repeat(3, 1fr); }
        .v4-stage-button { min-height: 62px; grid-template-columns: 1fr; justify-items: center; padding: 0 4px; text-align: center; }
        .v4-stage-button span { display: none; }
        .v4-stage-button strong { font-size: 9px; }
        .v4-network { padding: 30px 0; }
        .v4-network-row { align-items: flex-start; flex-direction: column; gap: 22px; }
        .v4-network-label { flex: auto; }
        .v4-network-logos { width: 100%; justify-content: flex-start; flex-wrap: wrap; }
        .v4-network-logos img { max-width: 92px; max-height: 26px; }
        .v4-manifesto, .v4-platform, .v4-workflow, .v4-use-cases, .v4-governance, .v4-delivery { padding: 96px 0; }
        .v4-manifesto blockquote { font-size: clamp(42px, 11vw, 62px); }
        .v4-platform-grid { margin-top: 48px; gap: 36px; }
        .v4-story-tabs { grid-template-columns: 1fr; }
        .v4-story-tab { padding: 18px 4px; }
        .v4-story-tab small { display: block; }
        .v4-story-stage { min-height: auto; padding: 14px; }
        .v4-scene { min-height: auto; padding: 26px 4px 4px; }
        .v4-scene h3 { font-size: 31px; }
        .v4-connect-scene, .v4-playbook-scene { grid-template-columns: 1fr; }
        .v4-join { width: 1px; height: 42px; margin: 0 auto; }
        .v4-join::after { right: -3px; top: auto; bottom: 0; transform: rotate(135deg); }
        .v4-graph-scene { height: 330px; }
        .v4-graph-node { min-width: 94px; padding: 10px; }
        .v4-graph-node.center { left: 34%; }
        .v4-flow { grid-template-columns: 1fr; }
        .v4-flow-step, .v4-flow-step:nth-child(n+4) { min-height: auto; border-right: 0; border-top: 1px solid rgba(255,255,255,.14); }
        .v4-flow-step:first-child { border-top: 0; }
        .v4-flow-note { text-align: left; }
        .v4-control-list { grid-template-columns: 1fr; }
        .v4-delivery-head { align-items: flex-start; flex-direction: column; }
        .v4-delivery-steps { grid-template-columns: 1fr; }
        .v4-delivery-step { min-height: auto; border: 1px solid var(--line); }
        .v4-delivery-step:last-child { border: 1px solid var(--line); }
        .v4-closing { padding: 8px 8px 0; }
        .v4-closing-panel { min-height: 560px; padding: 70px 20px; border-radius: 18px 18px 0 0; }
        .v4-footer { margin: 0 8px 8px; border-radius: 0 0 18px 18px; }
        .v4-footer-grid { grid-template-columns: 1fr 1fr; gap: 44px 24px; }
        .v4-footer-brand { grid-column: 1 / -1; }
      }

      @media (max-width: 520px) {
        .v4-shell { width: min(100% - 30px, 1360px); }
        .v4-hero-content { padding-top: 58px; }
        .v4-hero h1 { font-size: 47px; }
        .v4-hero-mark { width: 440px; right: -170px; top: -70px; }
        .v4-section-title { font-size: 43px; }
        .v4-canvas-brand span { display: none; }
        .v4-case { min-height: 470px; padding: 22px; }
        .v4-control-list { border-left: 0; }
        .v4-control { border: 1px solid var(--line); }
        .v4-footer-grid { grid-template-columns: 1fr; }
        .v4-footer-brand { grid-column: auto; }
        .v4-footer-bottom { align-items: flex-start; flex-direction: column; gap: 12px; }
      }

      @media (prefers-reduced-motion: reduce) {
        html { scroll-behavior: auto !important; }
        .v4-landing *, .v4-landing *::before, .v4-landing *::after {
          scroll-behavior: auto !important;
          animation-duration: .01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: .01ms !important;
        }
        .v4-hero::after, .v4-case:is(:hover,:focus-visible), .v4-header-cta, .v4-button, .v4-button-ghost { animation: none !important; transform: none !important; }
      }
    `}</style>
  );
}

export function WayakHeader() {
  const [open, setOpen] = useState(false);
  const nav = [
    ["Platform", "/#platform"],
    ["How it works", "/#workflow"],
    ["Use cases", "/use-cases"],
    ["Controls", "/#security"],
    ["Services", "/services"],
  ];

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, []);

  return (
    <>
      <header className="v4-header">
        <div className="v4-header-left">
          <a href="/#top" aria-label="Wayak home">
            <img
              src={ASSET + "/logo_main_black.png"}
              alt="Wayak"
              style={{ height: 22, display: "block" }}
            />
          </a>
          <nav className="v4-nav" aria-label="Primary navigation">
            {nav.map(([label, href]) => (
              <a key={label} href={href}>
                {label}
              </a>
            ))}
          </nav>
        </div>
        <div className="v4-header-right">
          <a className="v4-header-login" href={LOGIN_URL}>
            Sign in
          </a>
          <a
            className="v4-header-cta"
            href={DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackLanding("landing_demo_click", { location: "v4-topbar" })
            }
          >
            Book a demo
          </a>
          <button
            className="v4-mobile-toggle"
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="v4-mobile-menu"
            onClick={() => setOpen((value) => !value)}
          >
            {open ? "×" : "≡"}
          </button>
        </div>
      </header>
      <nav
        id="v4-mobile-menu"
        className={"v4-mobile-nav" + (open ? " is-open" : "")}
        aria-label="Mobile navigation"
      >
        {nav.map(([label, href]) => (
          <a key={label} href={href} onClick={() => setOpen(false)}>
            {label}
            <ArrowUpRight size={14} />
          </a>
        ))}
        <div className="v4-mobile-actions">
          <a href={LOGIN_URL}>Sign in</a>
          <a
            href={DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              setOpen(false);
              trackLanding("landing_demo_click", {
                location: "v4-mobile-menu",
              });
            }}
          >
            Book a demo
          </a>
        </div>
      </nav>
    </>
  );
}

function OperationsCanvas() {
  return <ProductMonitorPreview />;
}

function Hero() {
  return (
    <section id="top" className="v4-hero-wrap">
      <div className="v4-hero">
        <WayakSignal className="v4-hero-mark" />
        <div className="v4-shell v4-hero-content">
          <Reveal className="v4-hero-copy" immediate>
            <div className="v4-eyebrow">The operating system for real work</div>
            <h1>Turn operational complexity into a system that learns.</h1>
            <p className="v4-hero-sub">
              Wayak connects enterprise context, gives agents the full picture,
              and turns recurring work into governed action.
            </p>
            <div className="v4-hero-actions">
              <a
                className="v4-button"
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackLanding("landing_demo_click", { location: "v4-hero" })
                }
              >
                Book a demo <ArrowUpRight size={15} />
              </a>
              <a className="v4-button-ghost" href="#platform">
                See the system work <ArrowRight size={15} />
              </a>
            </div>
          </Reveal>
          <Reveal delay={120} immediate>
            <OperationsCanvas />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function NetworkBand() {
  return (
    <section className="v4-network" aria-label="Wayak network">
      <div className="v4-shell v4-network-row">
        <p className="v4-network-label">Selected teams in the Wayak network</p>
        <div className="v4-network-logos">
          {CLIENT_LOGOS.map(({ name, src, h }) => (
            <img
              key={name}
              src={src}
              alt={name}
              title={name}
              style={{ height: Math.min(h, 32) }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="v4-manifesto">
      <div className="v4-shell">
        <Reveal className="v4-manifesto-copy">
          <div className="v4-eyebrow">The Wayak thesis</div>
          <blockquote>
            The hard part isn’t generating an answer. It’s giving AI enough
            context to <em>do the work</em>—and enough control to do it
            responsibly.
          </blockquote>
        </Reveal>
      </div>
    </section>
  );
}

function ConnectScene() {
  return (
    <div className="v4-scene" key="connect">
      <h3>Bring the operation into one working context.</h3>
      <p>
        Connect the systems and artifacts people already rely on. Wayak keeps
        the source visible while creating a shared model agents can use.
      </p>
      <SourcesPreview />
    </div>
  );
}

function UnderstandScene() {
  return (
    <div className="v4-scene" key="understand">
      <h3>Give agents the relationships, rules, and history.</h3>
      <p>
        The answer is only useful when the agent understands what the records
        mean, how they connect, and which evidence supports a decision.
      </p>
      <ModelPreview />
    </div>
  );
}

function OperateScene() {
  return (
    <div className="v4-scene" key="operate">
      <h3>Move from judgment to action without losing control.</h3>
      <p>
        Playbooks turn recurring work into visible steps. People stay in the
        loop where consequences matter, and the outcome remains traceable.
      </p>
      <OperatePreview />
    </div>
  );
}

function PlatformStory() {
  const [active, setActive] = useState<StageIndex>(0);
  const scenes = [
    <ConnectScene key="connect" />,
    <UnderstandScene key="understand" />,
    <OperateScene key="operate" />,
  ];

  return (
    <section id="platform" className="v4-platform">
      <div className="v4-shell">
        <Reveal>
          <div className="v4-eyebrow">One operating layer</div>
          <h2 className="v4-section-title">
            Between fragmented data and action that compounds.
          </h2>
          <p className="v4-section-intro">
            Wayak does three jobs together: connect the operation, model its
            context, and run work with the right controls.
          </p>
        </Reveal>

        <div className="v4-platform-grid">
          <div
            className="v4-story-tabs"
            role="tablist"
            aria-label="Platform stages"
          >
            {STAGES.map((stage, index) => (
              <button
                key={stage.key}
                type="button"
                role="tab"
                aria-selected={active === index}
                className={
                  "v4-story-tab" + (active === index ? " is-active" : "")
                }
                onClick={() => setActive(index as StageIndex)}
              >
                <span>{stage.number}</span>
                <span>
                  <strong>{stage.label}</strong>
                  <small>{stage.title}</small>
                </span>
              </button>
            ))}
          </div>

          <Reveal delay={100}>
            <div className="v4-story-stage">
              <div className="v4-story-stage-top">
                <span>WAYAK · {STAGES[active].label}</span>
                <span>{STAGES[active].number} / 03</span>
              </div>
              {scenes[active]}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WorkflowChapter() {
  const flowRef = useRef<HTMLDivElement>(null);
  const [flowActive, setFlowActive] = useState(false);

  useEffect(() => {
    const element = flowRef.current;
    if (
      !element ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setFlowActive(Boolean(entry?.isIntersecting)),
      { rootMargin: "-8% 0px -8%", threshold: 0.2 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      title: "Intake",
      body: "A document, record, or system event enters the queue.",
      icon: <FileText size={18} />,
    },
    {
      title: "Context",
      body: "Wayak links the account, policy, evidence, and history.",
      icon: <Network size={18} />,
    },
    {
      title: "Reason",
      body: "A specialized agent explains the exception and proposes an outcome.",
      icon: <BrainCircuit size={18} />,
    },
    {
      title: "Control",
      body: "High-impact action pauses for the right person to review.",
      icon: <UserCheck size={18} />,
    },
    {
      title: "Writeback",
      body: "The approved result and its reasoning return to the system of record.",
      icon: <History size={18} />,
    },
  ];

  return (
    <section id="workflow" className="v4-workflow">
      <div className="v4-shell">
        <Reveal>
          <div className="v4-eyebrow">See the system at work</div>
          <h2 className="v4-section-title">From signal to governed action.</h2>
          <p className="v4-section-intro">
            One visible path from a messy operational event to an outcome your
            team can understand, approve, and trust.
          </p>
        </Reveal>
        <Reveal delay={100}>
          <div
            ref={flowRef}
            className={`v4-flow${flowActive ? " is-active" : ""}`}
          >
            {steps.map((step, index) => (
              <div className="v4-flow-step" key={step.title}>
                <span>0{index + 1}</span>
                <div className="v4-flow-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </div>
            ))}
          </div>
          <p className="v4-flow-note">Nothing disappears into a black box.</p>
        </Reveal>
      </div>
    </section>
  );
}

function UseCases() {
  const cases = [
    {
      index: "01",
      slug: "manufacturing",
      name: "Manufacturing",
      title: "Catch cost and quality drift while there is still time to act.",
      href: "/use-cases/manufacturing",
      theme: "dark",
      meta: ["MES", "ERP", "Quality", "Daily variance"],
      icon: <Factory size={17} />,
    },
    {
      index: "02",
      slug: "insurance",
      name: "Insurance",
      title: "Move review work from intake to a traceable decision.",
      href: "/use-cases/insurance",
      theme: "warm",
      meta: ["Claims", "Policy docs", "Rules", "Review-ready case"],
      icon: <FileSearch size={17} />,
    },
    {
      index: "03",
      slug: "customer-service",
      name: "Customer service",
      title: "Resolve the queue with the full customer story in view.",
      href: "/use-cases/customer-service",
      theme: "light",
      meta: ["CRM", "Tickets", "Knowledge", "Grounded response"],
      icon: <Headphones size={17} />,
    },
  ];

  return (
    <section id="use-cases" className="v4-use-cases">
      <div className="v4-shell">
        <Reveal>
          <div className="v4-eyebrow">Where it starts</div>
          <h2 className="v4-section-title">
            A narrow workflow. A system that keeps learning.
          </h2>
          <p className="v4-section-intro">
            Start where operational friction is expensive, then reuse the
            context, controls, and patterns across the business.
          </p>
        </Reveal>
        <div className="v4-case-grid">
          {cases.map((item, index) => (
            <Reveal key={item.name} delay={index * 90}>
              <a className={"v4-case " + item.theme} href={item.href}>
                <div className="v4-case-index">
                  <span>
                    {item.index} · {item.name}
                  </span>
                  {item.icon}
                </div>
                <IndustrySignal slug={item.slug} density="home" />
                <h3>{item.title}</h3>
                <p>
                  See the sources, agent judgment, human control, and output.
                </p>
                <div className="v4-case-meta">
                  {item.meta.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </a>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120} style={{ marginTop: 34 }}>
          <a className="v4-inline-link" href="/use-cases">
            Explore all ten use cases <ArrowUpRight size={14} />
          </a>
        </Reveal>
      </div>
    </section>
  );
}

function Governance() {
  const controls = [
    {
      title: "Source-aware answers",
      body: "Keep the evidence and source records connected to the result.",
      icon: <Link2 size={18} />,
    },
    {
      title: "Human approval gates",
      body: "Route consequential decisions to people before action continues.",
      icon: <UserCheck size={18} />,
    },
    {
      title: "Role-scoped workspaces",
      body: "Keep data, agents, and workflows inside the right organizational boundary.",
      icon: <LockKeyhole size={18} />,
    },
    {
      title: "Visible run history",
      body: "Inspect how a playbook moved from trigger to outcome.",
      icon: <History size={18} />,
    },
  ];

  return (
    <section id="security" className="v4-governance">
      <div className="v4-shell v4-governance-grid">
        <Reveal>
          <div className="v4-eyebrow">Control plane</div>
          <h2 className="v4-section-title">
            Autonomy without surrendering control.
          </h2>
          <p className="v4-section-intro">
            The system is designed to show its work, preserve organizational
            boundaries, and involve people where judgment belongs.
          </p>
        </Reveal>
        <Reveal delay={100}>
          <div className="v4-control-list">
            {controls.map((control) => (
              <div className="v4-control" key={control.title}>
                <span className="v4-control-icon">{control.icon}</span>
                <h3>{control.title}</h3>
                <p>{control.body}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Delivery() {
  return (
    <section className="v4-delivery">
      <div className="v4-shell">
        <Reveal className="v4-delivery-head">
          <div>
            <div className="v4-eyebrow">From workflow to production</div>
            <h2 className="v4-section-title">
              The platform is broad. The first win should be narrow.
            </h2>
          </div>
          <a className="v4-inline-link" href="/services">
            Explore Wayak services <ArrowUpRight size={15} />
          </a>
        </Reveal>
        <div className="v4-delivery-steps">
          {[
            [
              "01",
              "Map the work",
              "Find the bottleneck, the real decision points, and the systems already involved.",
            ],
            [
              "02",
              "Build and test",
              "Model the context, configure the agent, and prove the playbook against real exceptions.",
            ],
            [
              "03",
              "Deploy and improve",
              "Put the workflow into production, observe the runs, and compound what the system learns.",
            ],
          ].map(([number, title, body], index) => (
            <Reveal
              className="v4-delivery-step"
              key={String(title)}
              delay={index * 90}
            >
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClosingCTA() {
  return (
    <section className="v4-closing">
      <div className="v4-closing-panel">
        <Reveal className="v4-closing-content">
          <h2>Bring us the workflow everyone hates.</h2>
          <p>
            We’ll help turn the fragmented context, manual judgment, and
            recurring follow-up into a system your operation can build on.
          </p>
          <div className="v4-closing-actions">
            <a
              className="v4-button"
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackLanding("landing_demo_click", { location: "v4-final" })
              }
            >
              Book a demo <ArrowUpRight size={15} />
            </a>
            <a
              className="v4-button-ghost"
              href={SIGNUP_URL}
              onClick={() =>
                trackLanding("landing_signup_click", { location: "v4-final" })
              }
            >
              Create a workspace <ArrowRight size={15} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function WayakFooter() {
  return (
    <footer className="v4-footer">
      <div className="v4-shell">
        <div className="v4-footer-grid">
          <div className="v4-footer-brand">
            <img
              src={ASSET + "/logo_main_white.png"}
              alt="Wayak"
              style={{ height: 26, display: "block" }}
            />
            <p>
              Operational AI for connected context, governed decisions, and work
              that gets done.
            </p>
          </div>
          <div>
            <h3>Platform</h3>
            <div className="v4-footer-links">
              <a href="/#platform">How it works</a>
              <a href="/#workflow">Workflow</a>
              <a href="/#security">Controls</a>
              <a href={DOCS_URL}>Docs</a>
            </div>
          </div>
          <div>
            <h3>Use cases</h3>
            <div className="v4-footer-links">
              <a href="/use-cases">All use cases</a>
              <a href="/use-cases/manufacturing">Manufacturing</a>
              <a href="/use-cases/insurance">Insurance</a>
              <a href="/use-cases/customer-service">Customer service</a>
              <a href="/services">Services</a>
            </div>
          </div>
          <div>
            <h3>Company</h3>
            <div className="v4-footer-links">
              <a href={DEMO_URL}>Talk to us</a>
              <a href={LOGIN_URL}>Sign in</a>
              <a href="/privacy-policy">Privacy</a>
              <a href="/cookies">Cookies</a>
              <a href="https://www.linkedin.com/company/wayak-ai">LinkedIn</a>
            </div>
          </div>
        </div>
        <div className="v4-footer-bottom">
          <span>© 2026 Wayak® · All rights reserved</span>
          <span>Data → context → governed action</span>
        </div>
      </div>
    </footer>
  );
}

export default function LandingExperience() {
  return (
    <div className="v4-landing">
      <WayakMarketingStyles />
      <ProductPreviewStyles />
      <IndustrySignalStyles />
      <WayakSignalStyles />
      <WayakHeader />
      <main>
        <Hero />
        <NetworkBand />
        <Manifesto />
        <PlatformStory />
        <WorkflowChapter />
        <UseCases />
        <Governance />
        <Delivery />
        <ClosingCTA />
      </main>
      <WayakFooter />
    </div>
  );
}
